import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { 
  Calendar, Clock, Plus, Edit, Trash2, ChevronLeft, ChevronRight, 
  Users, Settings, Save, Loader2, Download, Printer, Copy
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const COLORS = [
  "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", 
  "#ec4899", "#06b6d4", "#84cc16", "#6366f1", "#14b8a6"
];

const ShiftPage = () => {
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [schedule, setSchedule] = useState([]);
  
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: "", code: "", start_time: "09:00", end_time: "18:00", break_hours: 1, color: "#10b981"
  });
  const [savingTemplate, setSavingTemplate] = useState(false);
  
  const [selectedCell, setSelectedCell] = useState(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignForm, setAssignForm] = useState({ shift_template_id: "", is_holiday: false, note: "" });
  const [savingAssign, setSavingAssign] = useState(false);
  
  const [activeTab, setActiveTab] = useState("schedule");

  useEffect(() => {
    fetchEmployees();
    fetchTemplates();
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, [selectedYear, selectedMonth]);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`);
      setEmployees(res.data);
    } catch (error) {
      toast.error("従業員情報の取得に失敗しました");
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/shifts/templates`);
      setTemplates(res.data);
    } catch (error) {
      console.error("Templates fetch error:", error);
    }
  };

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/shifts/schedule?year=${selectedYear}&month=${selectedMonth}`);
      setSchedule(res.data);
    } catch (error) {
      console.error("Schedule fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Template CRUD
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        name: template.name,
        code: template.code,
        start_time: template.start_time,
        end_time: template.end_time,
        break_hours: template.break_hours,
        color: template.color
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ name: "", code: "", start_time: "09:00", end_time: "18:00", break_hours: 1, color: "#10b981" });
    }
    setShowTemplateDialog(true);
  };

  const saveTemplate = async () => {
    if (!templateForm.name || !templateForm.code) {
      toast.error("名前とコードを入力してください");
      return;
    }
    setSavingTemplate(true);
    try {
      if (editingTemplate) {
        await axios.put(`${API}/shifts/templates/${editingTemplate.id}`, templateForm);
        toast.success("テンプレートを更新しました");
      } else {
        await axios.post(`${API}/shifts/templates`, templateForm);
        toast.success("テンプレートを作成しました");
      }
      fetchTemplates();
      setShowTemplateDialog(false);
    } catch (error) {
      toast.error("保存に失敗しました");
    } finally {
      setSavingTemplate(false);
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm("このテンプレートを削除しますか？")) return;
    try {
      await axios.delete(`${API}/shifts/templates/${templateId}`);
      toast.success("テンプレートを削除しました");
      fetchTemplates();
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  // Shift Assignment
  const openAssignDialog = (employeeId, date, existingShift = null) => {
    setSelectedCell({ employeeId, date, existingShift });
    setAssignForm({
      shift_template_id: existingShift?.shift_template_id || "",
      is_holiday: existingShift?.is_holiday || false,
      note: existingShift?.note || ""
    });
    setShowAssignDialog(true);
  };

  const saveAssignment = async () => {
    if (!selectedCell) return;
    setSavingAssign(true);
    try {
      await axios.post(`${API}/shifts/assign`, {
        employee_id: selectedCell.employeeId,
        date: selectedCell.date,
        shift_template_id: assignForm.shift_template_id || null,
        is_holiday: assignForm.is_holiday,
        note: assignForm.note
      });
      toast.success("シフトを割り当てました");
      fetchSchedule();
      setShowAssignDialog(false);
    } catch (error) {
      toast.error("割り当てに失敗しました");
    } finally {
      setSavingAssign(false);
    }
  };

  const deleteAssignment = async () => {
    if (!selectedCell) return;
    try {
      await axios.delete(`${API}/shifts/assign/${selectedCell.employeeId}/${selectedCell.date}`);
      toast.success("シフトを削除しました");
      fetchSchedule();
      setShowAssignDialog(false);
    } catch (error) {
      toast.error("削除に失敗しました");
    }
  };

  // Quick assign with click
  const quickAssign = async (employeeId, date, templateId) => {
    try {
      await axios.post(`${API}/shifts/assign`, {
        employee_id: employeeId,
        date: date,
        shift_template_id: templateId,
        is_holiday: false
      });
      fetchSchedule();
    } catch (error) {
      toast.error("割り当てに失敗しました");
    }
  };

  // Copy week
  const copyWeekShifts = async () => {
    // Get current week's shifts and copy to next week
    const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
    toast.info("週のシフトをコピーする機能は準備中です");
  };

  // Generate calendar days
  const getDaysInMonth = () => {
    const days = [];
    const firstDay = new Date(selectedYear, selectedMonth - 1, 1);
    const lastDay = new Date(selectedYear, selectedMonth, 0);
    const daysInMonth = lastDay.getDate();
    
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedYear, selectedMonth - 1, i);
      days.push({
        day: i,
        date: `${selectedYear}-${String(selectedMonth).padStart(2, "0")}-${String(i).padStart(2, "0")}`,
        dayOfWeek: date.getDay(),
        isWeekend: date.getDay() === 0 || date.getDay() === 6
      });
    }
    return days;
  };

  const days = getDaysInMonth();
  const dayNames = ["日", "月", "火", "水", "木", "金", "土"];

  // Get shift for a specific employee and date
  const getShift = (employeeId, date) => {
    const empSchedule = schedule.find(s => s.employee_id === employeeId);
    if (!empSchedule) return null;
    return empSchedule.shifts[date] || null;
  };

  const getTemplateById = (templateId) => {
    return templates.find(t => t.id === templateId);
  };

  const prevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedYear(selectedYear - 1);
      setSelectedMonth(12);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const nextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedYear(selectedYear + 1);
      setSelectedMonth(1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold mb-1">シフト管理</h1>
          <p className="text-slate-400 text-sm">従業員のシフトスケジュールを管理</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant={activeTab === "schedule" ? "default" : "outline"} 
            size="sm"
            onClick={() => setActiveTab("schedule")}
            className={activeTab === "schedule" ? "bg-emerald-600" : "border-slate-600"}
          >
            <Calendar className="w-4 h-4 mr-1" />
            スケジュール
          </Button>
          <Button 
            variant={activeTab === "templates" ? "default" : "outline"} 
            size="sm"
            onClick={() => setActiveTab("templates")}
            className={activeTab === "templates" ? "bg-emerald-600" : "border-slate-600"}
          >
            <Settings className="w-4 h-4 mr-1" />
            テンプレート
          </Button>
        </div>
      </div>

      {activeTab === "templates" && (
        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-white">シフトテンプレート</CardTitle>
                <CardDescription className="text-slate-400">
                  早番、遅番、夜勤などのシフトパターンを定義
                </CardDescription>
              </div>
              <Button onClick={() => openTemplateDialog()} size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-1" />
                新規作成
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {templates.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Clock className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p>テンプレートがありません</p>
                <p className="text-sm">「新規作成」ボタンから追加してください</p>
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {templates.map(template => (
                  <div key={template.id} className="p-4 bg-slate-700 rounded-lg border-l-4" style={{ borderLeftColor: template.color }}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h3 className="font-medium text-white">{template.name}</h3>
                        <Badge className="mt-1" style={{ backgroundColor: template.color }}>{template.code}</Badge>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openTemplateDialog(template)} className="h-8 w-8 p-0">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteTemplate(template.id)} className="h-8 w-8 p-0 text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="text-sm text-slate-300 space-y-1">
                      <p><Clock className="w-3 h-3 inline mr-1" />{template.start_time} - {template.end_time}</p>
                      <p>休憩: {template.break_hours}時間</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === "schedule" && (
        <>
          {/* Month Navigation */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={prevMonth} className="border-slate-600">
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <h2 className="text-xl font-bold px-4">{selectedYear}年{selectedMonth}月</h2>
              <Button variant="outline" size="sm" onClick={nextMonth} className="border-slate-600">
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex gap-2">
              {templates.slice(0, 5).map(t => (
                <Badge key={t.id} style={{ backgroundColor: t.color }} className="cursor-help" title={`${t.name} (${t.start_time}-${t.end_time})`}>
                  {t.code}
                </Badge>
              ))}
            </div>
          </div>

          {/* Shift Schedule Table */}
          <Card className="bg-slate-800 border-slate-700 overflow-hidden">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : employees.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <Users className="w-10 h-10 mx-auto mb-3 opacity-50" />
                  <p>従業員がいません</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-700">
                        <th className="sticky left-0 z-10 bg-slate-700 px-3 py-2 text-left font-medium text-white min-w-[120px]">
                          従業員
                        </th>
                        {days.map(d => (
                          <th 
                            key={d.date} 
                            className={`px-1 py-2 text-center font-medium min-w-[36px] ${
                              d.isWeekend ? 'bg-slate-600' : ''
                            } ${d.dayOfWeek === 0 ? 'text-red-400' : d.dayOfWeek === 6 ? 'text-blue-400' : 'text-white'}`}
                          >
                            <div className="text-xs">{dayNames[d.dayOfWeek]}</div>
                            <div>{d.day}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {employees.map(emp => (
                        <tr key={emp.id} className="border-t border-slate-700 hover:bg-slate-750">
                          <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 font-medium text-white">
                            <div className="truncate max-w-[100px]" title={emp.name}>{emp.name}</div>
                            <div className="text-xs text-slate-400">{emp.employee_code}</div>
                          </td>
                          {days.map(d => {
                            const shift = getShift(emp.id, d.date);
                            const template = shift?.shift_template_id ? getTemplateById(shift.shift_template_id) : null;
                            
                            return (
                              <td 
                                key={d.date}
                                className={`px-1 py-1 text-center cursor-pointer hover:bg-slate-600 transition-colors ${
                                  d.isWeekend ? 'bg-slate-700/50' : ''
                                }`}
                                onClick={() => openAssignDialog(emp.id, d.date, shift)}
                              >
                                {shift ? (
                                  shift.is_holiday ? (
                                    <Badge className="bg-slate-500 text-xs">休</Badge>
                                  ) : (
                                    <Badge 
                                      className="text-xs"
                                      style={{ backgroundColor: template?.color || '#6b7280' }}
                                    >
                                      {shift.shift_code || '?'}
                                    </Badge>
                                  )
                                ) : (
                                  <span className="text-slate-600">-</span>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Template Buttons */}
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-4">
              <h4 className="text-sm font-medium text-white mb-3">クイック割り当て</h4>
              <p className="text-xs text-slate-400 mb-3">表のセルをクリックしてシフトを割り当て、または以下のテンプレートを選択</p>
              <div className="flex flex-wrap gap-2">
                {templates.map(t => (
                  <Badge 
                    key={t.id} 
                    className="cursor-pointer hover:opacity-80 transition-opacity py-1.5 px-3"
                    style={{ backgroundColor: t.color }}
                  >
                    {t.code}: {t.name} ({t.start_time}-{t.end_time})
                  </Badge>
                ))}
                <Badge className="bg-slate-500 cursor-pointer hover:opacity-80 py-1.5 px-3">
                  休: 公休日
                </Badge>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent className="max-w-md bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>{editingTemplate ? "テンプレート編集" : "新規テンプレート"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>名前</Label>
                <Input 
                  value={templateForm.name} 
                  onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="例: 早番"
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>コード</Label>
                <Input 
                  value={templateForm.code} 
                  onChange={(e) => setTemplateForm({ ...templateForm, code: e.target.value })}
                  placeholder="例: A"
                  maxLength={3}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>開始時間</Label>
                <Input 
                  type="time"
                  value={templateForm.start_time} 
                  onChange={(e) => setTemplateForm({ ...templateForm, start_time: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
              <div className="space-y-2">
                <Label>終了時間</Label>
                <Input 
                  type="time"
                  value={templateForm.end_time} 
                  onChange={(e) => setTemplateForm({ ...templateForm, end_time: e.target.value })}
                  className="bg-slate-700 border-slate-600"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>休憩時間（時間）</Label>
              <Input 
                type="number"
                step="0.5"
                min="0"
                value={templateForm.break_hours} 
                onChange={(e) => setTemplateForm({ ...templateForm, break_hours: parseFloat(e.target.value) || 0 })}
                className="bg-slate-700 border-slate-600"
              />
            </div>
            <div className="space-y-2">
              <Label>カラー</Label>
              <div className="flex gap-2 flex-wrap">
                {COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 transition-transform ${
                      templateForm.color === color ? 'border-white scale-110' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setTemplateForm({ ...templateForm, color })}
                  />
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowTemplateDialog(false)} className="flex-1 border-slate-600">
                キャンセル
              </Button>
              <Button onClick={saveTemplate} disabled={savingTemplate} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {savingTemplate ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                保存
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-sm bg-slate-800 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>シフト割り当て</DialogTitle>
          </DialogHeader>
          {selectedCell && (
            <div className="space-y-4">
              <div className="text-sm text-slate-300">
                <p>従業員: {employees.find(e => e.id === selectedCell.employeeId)?.name}</p>
                <p>日付: {selectedCell.date}</p>
              </div>
              
              <div className="space-y-2">
                <Label>シフト</Label>
                <Select 
                  value={assignForm.shift_template_id} 
                  onValueChange={(v) => setAssignForm({ ...assignForm, shift_template_id: v, is_holiday: false })}
                >
                  <SelectTrigger className="bg-slate-700 border-slate-600">
                    <SelectValue placeholder="シフトを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(t => (
                      <SelectItem key={t.id} value={t.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: t.color }} />
                          {t.code}: {t.name} ({t.start_time}-{t.end_time})
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_holiday"
                  checked={assignForm.is_holiday}
                  onChange={(e) => setAssignForm({ ...assignForm, is_holiday: e.target.checked, shift_template_id: "" })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_holiday">公休日</Label>
              </div>

              <div className="space-y-2">
                <Label>メモ</Label>
                <Input 
                  value={assignForm.note} 
                  onChange={(e) => setAssignForm({ ...assignForm, note: e.target.value })}
                  placeholder="任意"
                  className="bg-slate-700 border-slate-600"
                />
              </div>

              <div className="flex gap-2 pt-2">
                {selectedCell.existingShift && (
                  <Button variant="outline" onClick={deleteAssignment} className="border-red-600 text-red-400 hover:bg-red-600 hover:text-white">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
                <Button variant="outline" onClick={() => setShowAssignDialog(false)} className="flex-1 border-slate-600">
                  キャンセル
                </Button>
                <Button onClick={saveAssignment} disabled={savingAssign} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  {savingAssign ? <Loader2 className="w-4 h-4 animate-spin" /> : "保存"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ShiftPage;
